// web/src/lib/api.ts

const BASE = ''  // same origin — no CORS needed

let accessToken: string | null = null

export function setTokens(access: string) {
  accessToken = access
  // No localStorage for security — token lives only in memory
}

export function clearTokens() {
  accessToken = null
}

export function getAccessToken(): string | null {
  return accessToken
}

// ApiError carries the server's structured error response so React Query
// onError handlers and toasts can show the actual message instead of a bare
// "Request failed" placeholder. Throwing raw Response objects (old behaviour)
// meant error.message was always "[object Response]".
export class ApiError extends Error {
  status: number
  code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
    this.name = 'ApiError'
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'request_failed'
  let message = res.statusText || `HTTP ${res.status}`
  try {
    const body = await res.json() as { error?: { code?: string; message?: string } }
    if (body?.error) {
      if (body.error.code) code = body.error.code
      if (body.error.message) message = body.error.message
    }
  } catch {
    // body not JSON — keep the statusText
  }
  return new ApiError(res.status, code, message)
}

// Single-flight refresh. The refresh token is single-use and rotated
// server-side, so concurrent 401s (a dashboard fires several API calls at
// once when the 15-min access token expires) must NOT each POST /refresh —
// only the first would consume the token, the rest would 401 and bounce the
// user to /login. Instead every caller awaits the same in-flight refresh.
let refreshInFlight: Promise<boolean> | null = null

function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const refreshRes = await fetch(BASE + '/api/auth/refresh', {
        method: 'POST',
        credentials: 'include', // sends the HttpOnly cookie
      })
      if (!refreshRes.ok) return false
      const data = await refreshRes.json()
      setTokens(data.access_token)
      return true
    } catch {
      return false
    }
  })()
  // Release the cached promise once it settles so the next expiry can refresh.
  void refreshInFlight.finally(() => { refreshInFlight = null })
  return refreshInFlight
}

async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  let res = await fetch(BASE + url, { ...init, headers })

  // Try refresh on 401 — browser auto-sends the HttpOnly cookie. The refresh
  // is single-flight (see refreshAccessToken) so parallel 401s share one call.
  //
  // Public auth endpoints are exempt: a 401 from /api/auth/login (wrong
  // password) or an invite accept is an expected result the caller renders
  // inline, NOT an expired session. Reloading to /login here would wipe the
  // form's error message before the user could read it.
  const isPublicAuth = url.startsWith('/api/auth/') || url.startsWith('/api/invites/')
  if (res.status === 401 && !isPublicAuth) {
    const refreshed = await refreshAccessToken()
    if (refreshed && accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
      res = await fetch(BASE + url, { ...init, headers })
    } else {
      clearTokens()
      window.location.href = '/login'
      throw new ApiError(401, 'session_expired', 'Session expired')
    }
  }

  return res
}

async function unwrap<T>(promise: Promise<Response>): Promise<T> {
  const r = await promise
  if (!r.ok) throw await toApiError(r)
  if (r.status === 204) return undefined as T
  return r.json() as Promise<T>
}

export const api = {
  get: <T = unknown>(url: string) => unwrap<T>(fetchWithAuth(url)),
  post: <T = unknown>(url: string, body?: unknown) =>
    unwrap<T>(fetchWithAuth(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })),
  patch: <T = unknown>(url: string, body: unknown) =>
    unwrap<T>(fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })),
  del: <T = void>(url: string) =>
    unwrap<T>(fetchWithAuth(url, { method: 'DELETE' })),
}

// Transforms API helpers
export const transforms = {
  test: (body: { body: string; headers?: Record<string, string>; transforms: unknown[] }) =>
    api.post('/api/transforms/test', body) as Promise<{ body: string; headers?: Record<string, string> }>,
}

// Buffer/Pause API helpers
export const buffer = {
  pause: (endpointId: string) => api.post(`/api/endpoints/${endpointId}/buffer/pause`),
  resume: (endpointId: string) => api.post(`/api/endpoints/${endpointId}/buffer/resume`),
  status: (endpointId: string) => api.get(`/api/endpoints/${endpointId}/buffer/status`) as Promise<{ paused: boolean; count: number }>,
  releaseOne: (endpointId: string) => api.post(`/api/endpoints/${endpointId}/buffer/release-one`) as Promise<{ event: Record<string, unknown>; delivery: { status?: number; time_ms?: number; error?: string } }>,
  releaseAll: (endpointId: string) => api.post(`/api/endpoints/${endpointId}/buffer/release-all`),
  flush: (endpointId: string) => api.del(`/api/endpoints/${endpointId}/buffer/flush`),
  requeue: (endpointId: string, eventData: string) => api.post(`/api/endpoints/${endpointId}/buffer/requeue`, { event_data: eventData }),
}

// GDPR data export (Art. 20). Returns the raw ZIP blob — the api.* helpers
// all unwrap JSON, which would corrupt a binary body.
export async function downloadAccountExport(): Promise<Blob> {
  const res = await fetchWithAuth('/api/account/export')
  if (!res.ok) throw await toApiError(res)
  return res.blob()
}

// Billing API helpers
//
// events_used is nullable: the backend returns null when ClickHouse is
// unreachable (paired with usage_status='count_unavailable') so the UI
// can distinguish "you have 0 events" from "we can't count right now".
export interface BillingQuota {
  plan: string
  events_used: number | null
  events_limit: number
  trial_ends_at: string | null
  grace_ends_at: string | null
  usage_status?: 'ok' | 'count_unavailable'
}

export const billing = {
  getQuota: () => api.get<BillingQuota>('/api/billing/quota'),
  createCheckout: (plan: 'starter' | 'pro') => api.post<{ url: string }>('/api/billing/checkout', { plan }),
  createPortalSession: () => api.post<{ url: string }>('/api/billing/portal'),
}
